/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/JobQaWorkspaceActions.tsx
 * FILE_ID: JOBHUB-QA-WORKSPACE-ACTIONS-001
 * PURPOSE: Wire the one AREAS toolbar to the current selection scope.
 * LANDMINE: Per-row assign/move were removed on operator instruction; every
 *      verb still lives here. ITP export is the one door, not this bar.
 */
import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useQaPhotoPrefs } from '@quotech/ui'
import type { QaAreaExecution, QaFloor } from '../../qa/qaApi'
import { areaItems, checklistGroups, normalizeList } from '../../qa/qaApiHelpers'
import { moveSibling, toSortOrderPayload } from '../../qa/checklistTable'
import { QaBelongsToLevelBox } from './QaBelongsToLevelBox'
import { QaBulkAssignBar } from './QaBulkAssignBar'
import { QaMoveDialog } from './QaMoveDialog'
import { ContextMenu } from '@quotech/ui/contextMenu/positioned'
import { QA_PENDING_DELETE_NOTICE, qaErrorMessage } from './qaTabSelectors'
import { selectedQaUnion, uniqueIds, qaDeleteConfirm, qaDeleteTargets } from './qaItpScope'
import { holdQaArea, releaseQaArea, reorderAreaItems } from './qaExecutionCache'
import { applyQaAssign, applyQaDelete, type QaDeleteResult } from './qaSelectionWrites'
import { useQaItpExecutions } from './useQaItpExecutions'
import type { JobQaTabController } from './useJobQaTabController'

export function JobQaWorkspaceActions({
  controller,
  onStartRename,
}: {
  controller: JobQaTabController
  onStartRename: (scope: 'areas' | 'checklists', id: string) => void
}) {
  const { api, assignee, selection, tree, workspace } = controller
  const canBuild = controller.canBuild !== false
  const photoPrefs = useQaPhotoPrefs()
  const queryClient = useQueryClient()
  const [moving, setMoving] = useState(false)
  const [otherLevels, setOtherLevels] = useState<string[]>([])
  const loadLevels = useCallback(() => {
    void api.listGroupNames(controller.props.jobId).then(setOtherLevels).catch(() => setOtherLevels([]))
  }, [api, controller.props.jobId])
  useEffect(() => {
    if (!controller.containedInNonce) return
    loadLevels()
  }, [controller.containedInNonce, loadLevels])
  const [confirm, setConfirm] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const areas = normalizeList<QaAreaExecution>(workspace.areasQuery.data)
  const floors = normalizeList<QaFloor>(workspace.floorsQuery.data)
  const execution = workspace.areaExecQuery.data
  const groups = execution ? checklistGroups(execution) : []
  const items = execution ? areaItems(execution) : []
  const areaIds = areas.map((area) => area.id)
  const checklistIds = groups.map((group) => group.checklist.id)
  const itemIds = items.map((item) => item.id)
  const locked = execution?.status === 'locked'
  const canOrganise = (assignee.canAssign || canBuild) && !locked
  const scope = selection.scope ?? 'areas'
  const runMode = workspace.areaMode === 'run'
  const runGroup = runMode && execution
    ? checklistGroups(execution, undefined)
      .find((group) => group.checklist.id === workspace.activeChecklistId)
    : undefined
  const runPool = runGroup ? runGroup.items.map((item) => item.id) : []
  const runIncluded = runPool.filter((id) => (
    selection.isCheckIncluded(id, workspace.activeChecklistId ?? undefined, execution?.id)
  ))
  const barScope = runMode ? 'items' : scope === 'checks' ? 'items' : scope
  const pool = scope === 'checklists' ? checklistIds : scope === 'checks' ? itemIds : areaIds
  const visibleIds = selection.visibleOf(
    scope === 'checks' ? 'checks' : scope,
    pool,
  )
  const label = runMode ? 'checks' : scope === 'checklists' ? 'checklists' : scope === 'checks' ? 'checks' : 'areas'
  const allSelected = runMode
    ? runPool.length > 0 && runIncluded.length === runPool.length
    : visibleIds.length > 0 && visibleIds.length === pool.length
  const someSelected = runMode
    ? runIncluded.length > 0 && runIncluded.length < runPool.length
    : visibleIds.length > 0 && !allSelected
  // Union across all three scopes (mockup "Select" rules): the count and the
  // bulk assign cover EVERYTHING ticked, not just the most specific scope.
  // useQaItpExecutions is react-query-cached; the section already runs it.
  const { executions } = useQaItpExecutions(areaIds, api, workspace.areaExecutionsReady)
  const union = selectedQaUnion(selection.visibleOf, executions, areaIds, selection.excludedChecks)
  // Reused from: qaItpScope.ts (selectedQaUnion)
  const deleteTargets = qaDeleteTargets(union, executions)
  const unionTotal = union.areas.length + union.checklists.length + union.checks.length
  // Checklists have no assignee of their own — a ticked checklist assigns its
  // checks. Selected areas keep their own area_ids path (assigns the area).
  const assignItemIds = uniqueIds([
    ...union.checks,
    ...executions.flatMap((area) => checklistGroups(area)
      .filter((group) => union.checklists.includes(group.checklist.id))
      .flatMap((group) => group.items.map((row) => row.id))),
  ])

  const untickDeleted = (result: QaDeleteResult) => {
    for (const id of result.deletedItems) {
      if (selection.isSelected('checks', id)) selection.toggle('checks', id)
    }
    for (const id of result.deletedChecklists) {
      if (selection.isSelected('checklists', id)) selection.toggle('checklists', id)
    }
    for (const id of result.deletedAreas) {
      if (selection.isSelected('areas', id)) selection.toggle('areas', id)
    }
  }

  const destroy = useMutation({
    mutationFn: () => applyQaDelete({
      api,
      queryClient,
      inspectionId: workspace.activeInspectionId,
      executions,
      targets: deleteTargets,
    }),
    onSuccess: (result) => {
      if (workspace.activeAreaId && result.deletedAreas.includes(workspace.activeAreaId)) {
        const list = normalizeList<QaAreaExecution>(queryClient.getQueryData(['qa-areas', workspace.activeInspectionId]))
        workspace.selectArea(list[0]?.id ?? null)
      }
      if (result.failed > 0) {
        setDeleteError('Could not delete.')
        return
      }
      if (result.pendingItems.length > 0) {
        setDeleteError(QA_PENDING_DELETE_NOTICE)
        untickDeleted(result)
        return
      }
      setConfirm(null)
      setDeleteError(null)
      selection.clear()
    },
    onError: (error: unknown) => {
      setDeleteError(qaErrorMessage(error, 'Could not delete.'))
    },
  })

  const moveItems = (direction: -1 | 1) => {
    const selectedId = union.checks[0]
    if (!selectedId || union.checks.length !== 1) return
    const owner = executions.find((area) => areaItems(area).some((item) => item.id === selectedId))
    if (!owner) return
    const flat = areaItems(owner)
    const index = flat.findIndex((item) => item.id === selectedId)
    const next = moveSibling(flat, index, direction)
    if (next.map((item) => item.id).join() === flat.map((item) => item.id).join()) return
    const ids = next.map((item) => item.id)
    const previous = queryClient.getQueryData<QaAreaExecution>(['qa-area-exec', owner.id])
    holdQaArea(owner.id)
    if (previous) {
      // Reused from: packages/job-hub/src/jobs/tabs/job-qa/qaExecutionCache.ts (reorderAreaItems)
      queryClient.setQueryData(['qa-area-exec', owner.id], reorderAreaItems(previous, ids))
    }
    if (owner.id !== workspace.activeAreaId) {
      const checklistId = flat.find((item) => item.id === selectedId)?.checklist_instance ?? null
      workspace.focusArea(owner.id, checklistId)
    }
    void api.reorderItems(toSortOrderPayload(ids)).then(
      () => releaseQaArea(owner.id),
      () => {
        if (previous) queryClient.setQueryData(['qa-area-exec', owner.id], previous)
        releaseQaArea(owner.id)
      },
    )
  }

  return (
    <div className="min-w-0 flex-1 space-y-1">
      <QaBulkAssignBar
        scope={barScope}
        label={label}
        candidates={assignee.candidates}
        searchCandidates={assignee.searchCandidates}
        canAssign={assignee.canAssign && (union.areas.length > 0 || assignItemIds.length > 0 || unionTotal === 0)}
        canOrganise={canOrganise || (scope === 'areas' && assignee.canAssign)}
        count={unionTotal}
        allSelected={allSelected}
        someSelected={someSelected}
        busy={workspace.bulkAssign.isPending || destroy.isPending}
        selectDisabled={runMode && runPool.length === 0}
        onSelectAll={(next) => {
          if (runMode) {
            if (next) selection.addChecks(runPool)
            else selection.removeChecks(runPool)
            return
          }
          const nextScope = scope === 'checks' ? 'checks' : scope
          if (next) selection.selectAll(nextScope, pool)
          else selection.clear()
        }}
        onClear={selection.clear}
        onAssign={(userId, dueDate, name) => {
          if (userId == null) return
          if (union.areas.length === 0 && assignItemIds.length === 0) return
          void applyQaAssign({
            queryClient,
            inspectionId: workspace.activeInspectionId,
            executions,
            areaIds: union.areas,
            itemIds: assignItemIds,
            person: { userId, name: name || '', dueDate },
          }).then((held) => {
            workspace.bulkAssign.mutate({
              ...(union.areas.length ? { area_ids: union.areas } : {}),
              ...(assignItemIds.length ? { item_ids: assignItemIds } : {}),
              user_id: userId,
              ...(dueDate ? { due_date: dueDate } : {}),
            }, {
              onSuccess: () => held.release(),
              onError: () => { held.restore(); held.release() },
            })
          })
        }}
        onMove={!canBuild || scope === 'checks' ? undefined : () => setMoving(true)}
        onContainedIn={!canBuild || scope !== 'areas' ? undefined : (event) => {
          const rect = event.currentTarget.getBoundingClientRect()
          controller.setBelongsPoint({ x: rect.left, y: rect.bottom + 4 })
          loadLevels()
        }}
        onDelete={!canBuild ? undefined : () => {
          setDeleteError(null)
          setConfirm(qaDeleteConfirm(deleteTargets))
        }}
        onRename={canBuild && visibleIds.length === 1 && scope !== 'checks'
          ? () => onStartRename(scope, visibleIds[0])
          : undefined}
        onMoveUp={canBuild && scope === 'checks' ? () => moveItems(-1) : undefined}
        onMoveDown={canBuild && scope === 'checks' ? () => moveItems(1) : undefined}
        selectMode={photoPrefs.selectEnabled}
        onToggleSelectMode={() => photoPrefs.setSelectEnabled(!photoPrefs.selectEnabled)}
      />
      {confirm && (
        <div className="rounded-lg border border-border bg-card p-2" data-testid="qa-delete-confirm">
          <p className="px-1 pb-2 text-[11px] text-foreground">{confirm}</p>
          {deleteError ? <p className="px-1 pb-2 text-[11px] text-destructive">{deleteError}</p> : null}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={destroy.isPending}
              onClick={() => destroy.mutate()}
              // Reused from: packages/job-hub/src/jobs/tabs/JobQaListChips.tsx (InspectionChip confirm Yes)
              className="inline-flex items-center gap-1 rounded-md bg-destructive px-2 py-1 text-[11px] font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              Delete
            </button>
            <button
              type="button"
              onClick={() => {
                setDeleteError(null)
                setConfirm(null)
              }}
              className="rounded-md px-2 py-1 text-[11px] text-muted-foreground"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {controller.belongsPoint ? (
        // Reused from: packages/job-hub/src/jobs/qa/qaPositionedMenu.tsx (ContextMenu)
        <ContextMenu
          x={controller.belongsPoint.x}
          y={controller.belongsPoint.y}
          onClose={() => controller.setBelongsPoint(null)}
        >
          <QaBelongsToLevelBox
            floors={floors}
            areas={areas}
            otherLevelNames={otherLevels}
            busy={tree.move.isPending}
            onCancel={() => controller.setBelongsPoint(null)}
            onSubmit={(destination) => {
              // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaTreeMoves.ts (tree.move)
              tree.move.mutate(
                { nodeType: 'area', nodeIds: union.areas, destination },
                { onSuccess: () => { controller.setBelongsPoint(null); selection.clear() } },
              )
            }}
          />
        </ContextMenu>
      ) : null}
      {moving && (
        <QaMoveDialog
          count={visibleIds.length}
          label={label}
          targets={scope === 'areas'
            ? floors.map((floor) => ({ id: floor.id, name: floor.floor_name }))
            : normalizeList<QaAreaExecution>(workspace.areasQuery.data)
              .filter((area) => area.id !== execution?.id)
              .map((area) => ({ id: area.id, name: area.area_name }))}
          allowNewLevel={scope === 'areas'}
          busy={tree.move.isPending}
          error={tree.error}
          onCancel={() => { setMoving(false); tree.dismissError() }}
          onMove={(destination) => {
            tree.move.mutate(
              {
                nodeType: scope === 'areas' ? 'area' : 'checklist_instance',
                nodeIds: scope === 'areas' ? union.areas : union.checklists,
                destination,
              },
              { onSuccess: () => { setMoving(false); selection.clear() } },
            )
          }}
        />
      )}
    </div>
  )
}
